import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Tier3SendAction } from '~/components/approvals';
import { Breadcrumbs, PageHeader } from '~/components/data';
import { PaymentAllocationModal, PeriodCloseBanner } from '~/components/domain';
import { ProjectRefChip } from '~/components/domain/project/ProjectRefChip';
import { Button } from '~/components/primitives';
import { allocatePayment, cancelPayment, fetchPayment, postPayment } from '~/lib/api/payments';
import { formatDate, formatMoney } from '~/lib/format';
import { canDo, useUserScope } from '~/lib/rbac/use-user-scope';
import shared from './_auth._shared.module.css';

/**
 * S14-FE.7 + S14-FE.8 — Payment detail + allocation modal (inline tab).
 */

export const Route = createFileRoute('/_auth/payments/$id/')({ component: PaymentDetailPage });

function PaymentDetailPage(): JSX.Element {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const [allocDialog, setAllocDialog] = useState(false);

  const query = useQuery({
    queryKey: ['payments', id],
    queryFn: () => fetchPayment(id),
    refetchInterval: 5_000,
  });

  const postMut = useMutation({
    mutationFn: () => postPayment(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['payments'] }),
  });
  const cancelMut = useMutation({
    mutationFn: () => cancelPayment(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['payments'] }),
  });
  const allocateMut = useMutation({
    mutationFn: (args: Parameters<typeof allocatePayment>[1]) => allocatePayment(id, args),
    onSuccess: () => {
      setAllocDialog(false);
      void qc.invalidateQueries({ queryKey: ['payments'] });
    },
  });

  const scope = useUserScope();
  // BE-10 (tails-sweep-2): `payment` заведён в каноне ABILITIES — прокси снят.
  const canEdit = canDo(scope, 'payment', 'update');
  const p = query.data;
  if (!p) {
    return (
      <div>
        <PageHeader title={m.phase2_payments_title()} />
        <div>{query.isLoading ? '…' : 'Not found'}</div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: m.phase2_payments_title(), to: '/payments' },
              { label: formatDate(p.payment_date) },
            ]}
          />
        }
        // FE-82: H1 uses external_ref (PAY-DRAFT-OUT-002) вместо date — stable identity.
        title={`${m.phase2_payments_title()} · ${(p as { external_ref?: string | null }).external_ref ?? formatDate(p.payment_date)}`}
        description={
          (p as { project_id?: string | null }).project_id ? (
            <ProjectRefChip projectId={(p as { project_id?: string | null }).project_id ?? null} />
          ) : undefined
        }
        actions={
          <>
            {p.state === 'draft' && canEdit && (
              <Link to="/payments/$id/edit" params={{ id }}>
                <Button variant="secondary">{m.common_button_edit()}</Button>
              </Link>
            )}
            {p.state === 'draft' && (
              <Button onClick={() => postMut.mutate()} loading={postMut.isPending}>
                {m.invoice_button_post()}
              </Button>
            )}
            {p.state === 'posted' && (
              <Button
                variant="ghost"
                onClick={() => cancelMut.mutate()}
                loading={cancelMut.isPending}
              >
                {m.invoice_button_cancel()}
              </Button>
            )}
            <Tier3SendAction
              label={m.invoice_button_push_1c()}
              subject={{ taskType: 'documents_push_1c', subjectKind: 'payment', payload: { id } }}
            />
          </>
        }
      />
      <PeriodCloseBanner scope="payment" />

      <div className={shared.statGrid4Lg}>
        <div>
          <div className={shared.statLabel}>{m.payment_column_amount()}</div>
          <div data-testid="payment-amount">{formatMoney(p.amount)}</div>
        </div>
        <div>
          <div className={shared.statLabel}>{m.payment_column_direction()}</div>
          <div>
            {p.direction === 'inflow'
              ? m.payment_direction_inflow()
              : m.payment_direction_outflow()}
          </div>
        </div>
        <div>
          <div className={shared.statLabel}>{m.payment_column_method()}</div>
          <div>{m.payment_method_label({ method: p.method })}</div>
        </div>
        <div>
          <div className={shared.statLabel}>{m.payment_column_state()}</div>
          <div>
            {/* FE-89: state as StatusBadge pill — bg + color по state + icon. */}
            {(() => {
              const isOk = p.state === 'posted';
              const isDanger = p.state === 'cancelled';
              const icon = isOk ? '✓' : isDanger ? '✗' : '◑';
              return (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '2px 10px',
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 500,
                    background: isOk
                      ? 'var(--ok-bg-15)'
                      : isDanger
                        ? 'var(--danger-bg-15)'
                        : 'var(--warn-bg-15)',
                    color: isOk
                      ? 'var(--color-ok)'
                      : isDanger
                        ? 'var(--color-danger)'
                        : 'var(--color-warn)',
                  }}
                >
                  <span aria-hidden="true">{icon}</span>
                  {m.payment_state_label({ state: p.state })}
                </span>
              );
            })()}
          </div>
        </div>
      </div>

      {/* FE-N Pass 72: detail card расширен — раньше только Amount/Direction/Method/State,
          теперь Counterparty + Date + External Ref + Notes + 1C ref + CF taxonomy + Source. */}
      <div className={shared.detailCard}>
        {/* access-control (F9) — компания записи. */}
        <div className={shared.detailRow}>
          <span className={shared.detailRowLabel}>{m.cp_field_company()}</span>
          <strong>{(p as { entity_name?: string | null }).entity_name ?? '—'}</strong>
        </div>
        <div className={shared.detailRow}>
          <span className={shared.detailRowLabel}>{m.payment_column_counterparty()}</span>
          <strong>
            {(p as { counterparty_name_ru?: string | null }).counterparty_name_ru ??
              (p as { counterparty_name_en?: string | null }).counterparty_name_en ??
              p.counterparty_id.slice(0, 8)}
          </strong>
        </div>
        <div className={shared.detailRow}>
          <span className={shared.detailRowLabel}>{m.payment_column_date()}</span>
          <strong>{formatDate(p.payment_date)}</strong>
        </div>
        {p.external_ref && (
          <div className={shared.detailRow}>
            <span className={shared.detailRowLabel}>{m.payment_detail_external_ref()}</span>
            <strong>{p.external_ref}</strong>
          </div>
        )}
        {p.bank_account_ref && (
          <div className={shared.detailRow}>
            <span className={shared.detailRowLabel}>{m.payment_detail_bank_account()}</span>
            <strong>{p.bank_account_ref}</strong>
          </div>
        )}
        {p.notes_ru && (
          <div className={shared.detailRow}>
            <span className={shared.detailRowLabel}>{m.payment_detail_notes()}</span>
            <strong>{p.notes_ru}</strong>
          </div>
        )}
        {p.onec_ref && (
          <div className={shared.detailRow}>
            <span className={shared.detailRowLabel}>{m.payment_detail_onec_ref()}</span>
            <strong>{p.onec_ref}</strong>
          </div>
        )}
        {p.cf_article && (
          <div className={shared.detailRow}>
            <span className={shared.detailRowLabel}>{m.payment_detail_cf_article()}</span>
            <strong>{p.cf_article}</strong>
          </div>
        )}
        {p.cf_group && (
          <div className={shared.detailRow}>
            <span className={shared.detailRowLabel}>{m.payment_detail_cf_group()}</span>
            <strong>{p.cf_group}</strong>
          </div>
        )}
        <div className={shared.detailRow}>
          <span className={shared.detailRowLabel}>{m.payment_column_source()}</span>
          <strong>{m.source_system_label({ code: p.source_system })}</strong>
        </div>
      </div>

      <section className={shared.elevatedCardMb}>
        <h2 className={shared.h2NoMargin}>{m.payment_allocate_dialog_title()}</h2>
        <Button onClick={() => setAllocDialog(true)} disabled={p.state !== 'posted'}>
          {m.payment_button_allocate()}
        </Button>
        <PaymentAllocationModal
          open={allocDialog}
          payment={p}
          onClose={() => setAllocDialog(false)}
          onSubmit={(args) => allocateMut.mutateAsync(args)}
          loading={allocateMut.isPending}
        />
        {p.allocations && p.allocations.length > 0 ? (
          <div className={shared.mt4}>
            {p.allocations.map((a) => (
              <div key={a.id} className={shared.rowBetween}>
                <span>
                  {a.invoice_id.slice(0, 8)} · {a.method}
                </span>
                <strong>{formatMoney(a.amount)}</strong>
              </div>
            ))}
          </div>
        ) : (
          <p className={shared.subtleHint}>
            {p.state === 'posted'
              ? m.payment_allocations_empty_hint()
              : m.payment_allocations_disabled_hint()}
          </p>
        )}
      </section>
      {/* EFE-13: last-modified footer with audit log link */}
      <div className={shared.subtle} style={{ marginTop: 'var(--space-3)' }}>
        {m.detail_last_updated()}: {formatDate(p.updated_at ?? p.created_at)}
        {' · '}
        <a href={`/admin/audit?entity_id=${id}`} className={shared.link}>
          {m.detail_view_audit_log()}
        </a>
      </div>
    </div>
  );
}
